import { useApiMutation, useApiPolling, useApiQuery } from "@/api";
import {
    generateFinanceLabMetrics,
    getTaskStatus,
    getFinanceLabMetrics,
} from ".";
import {
    GenerateFinanceLabMetricsPayload,
    FinanceLabStatus,
    FinanceLabField,
} from "./FinanceLab.model";
import { useCallback, useRef, useState } from "react";
import { PeriodKind } from "../Labeling/Account.model";
import { toast } from "@/ui";

export const useFinanceLab = (params?: {
    labels?: FinanceLabField[];
    periodKind?: PeriodKind;
}) => {
    const [displayMessage, setDisplayMessage] = useState<string>("");
    const pollCountRef = useRef<number>(0);

    const {
        data: task,
        isPolling,
        startPolling,
        stopPolling,
        isError: isTaskError,
        queryClient,
    } = useApiPolling(getTaskStatus, {
        onPollSuccess(data) {
            if (data?.meta?.displayMsg) {
                setDisplayMessage(data.meta.displayMsg);
                pollCountRef.current += 1;
                const duration = 5000 + pollCountRef.current * 1000;

                toast.updating("Refreshing memo...", {
                    description: data.meta.displayMsg,
                    duration,
                });
            }
            if (data?.state === FinanceLabStatus.Failure) {
                stopPolling();
                setDisplayMessage("");
                pollCountRef.current = 0;
                toast.error("Failed to generate metrics", {
                    description: "Please try again later",
                });
            }
            if (data?.state === FinanceLabStatus.Success) {
                stopPolling();
                setDisplayMessage("");
                pollCountRef.current = 0;

                queryClient?.invalidateQueries({
                    predicate: (query) => {
                        const queryKey = query.queryKey;
                        return (
                            Array.isArray(queryKey) &&
                            queryKey.some(
                                (key) =>
                                    typeof key === "string" &&
                                    key.includes("/hum-finance-lab/"),
                            )
                        );
                    },
                });

                toast.success("Refresh successful", {
                    description:
                        "Check the Financial Highlights for the latest numbers",
                });
            }
        },
    });

    const { data: metricsQuery, isLoading: isLoadingMetrics } = useApiQuery(
        getFinanceLabMetrics,
        {
            params: {
                labels: params?.labels || [],
                periodKind: params?.periodKind || PeriodKind.MONTHLY,
            },
            enabled: task?.state === FinanceLabStatus.Success,
        },
    );

    const { mutate: generateMetrics, isError: isGenerateMetricsError } =
        useApiMutation(generateFinanceLabMetrics, {
            onSuccess(data) {
                if (data.taskId) {
                    setDisplayMessage("");
                    startPolling(data);
                }
            },
        });

    const isError =
        isTaskError ||
        isGenerateMetricsError ||
        task?.state === FinanceLabStatus.Failure;

    const isSuccess = task?.state === FinanceLabStatus.Success;

    const stopTask = useCallback(() => {
        stopPolling();
    }, [stopPolling]);

    return {
        task,
        isPolling,
        startGenerateMetrics: (payload: GenerateFinanceLabMetricsPayload) =>
            generateMetrics(payload),
        stopPolling: stopTask,
        isError,
        isSuccess,
        displayMessage,
        metrics: metricsQuery,
        isLoadingMetrics: isLoadingMetrics,
    };
};
